import React, { useState, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useDropzone } from 'react-dropzone';
import SpinningCircle from './SpinningCircle';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowUpTrayIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { formatISO } from 'date-fns';

const ONE_KILOBYTE = 1024;
const MAX_FILES = 10000;
const MAX_FILE_SIZE = 64 * ONE_KILOBYTE;
const ONE_MEGABYTE = ONE_KILOBYTE * ONE_KILOBYTE;
const MAX_TOTAL_SIZE = 50 * ONE_MEGABYTE;

interface ImportNotesProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = 'upload' | 'topics' | 'done';

interface Topic {
  name: string;
  noteIds: number[];
  noteCount: number;
  selected?: boolean;
}

interface Message {
  text: string;
  type: 'info' | 'error';
}

const defaultMessage: Message = { text: '', type: 'info' };

const ImportNotes: React.FC<ImportNotesProps> = ({ isOpen, onClose, onSuccess }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [message, setMessage] = useState<Message>(defaultMessage);
  const { getAccessTokenSilently } = useAuth0();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > MAX_FILES) {
      setMessage({ text: `Maximum ${MAX_FILES} files allowed`, type: 'error' });
      return;
    }

    const oversizedFiles = acceptedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setMessage({ text: `Some files exceed the maximum size of ${MAX_FILE_SIZE / (1024)}KB`, type: 'error' });
      return;
    }

    const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      setMessage({ text: `Total size exceeds ${MAX_TOTAL_SIZE / (1024 * 1024)}MB`, type: 'error' });
      return;
    }

    setFiles(acceptedFiles);
    setMessage({ text: `${acceptedFiles.length} ${acceptedFiles.length === 1 ? 'file' : 'files'} selected`, type: 'info' });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md']
    },
    multiple: true,
  });

  const handleImport = async () => {
    if (files.length === 0) return;
    
    setIsImporting(true);
    try {
      window.analytics?.track("Settings | Import Notes Started");

      const { downloadZip } = await import('client-zip');
      const zipBlob = await downloadZip(files).blob();

      // Convert the zip blob to a base64 string
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]);
        };
      });
      reader.readAsDataURL(zipBlob);
      const base64Data = await base64Promise;

      // Send the zip to the API
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/notes/import/upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            zipFile: base64Data
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }

      const data = await response.json();
      const topics = Object.entries(data.newTopics)
        .map(([key, value]) => ({
          name: key,
          noteIds: value as number[],
          noteCount: (value as number[]).length,
          selected: (value as number[]).length > 1,
        }))
        .sort((a, b) => b.noteCount - a.noteCount);
      
      if (topics.length === 0) {
        setCurrentStep('done');
      } else {
        setTopics(topics);
        setCurrentStep('topics');
      }
      
      window.analytics?.track("Settings | Import Notes Succeeded", {
        fileCount: files.length
      });
    } catch (error) {
      console.error("Error during import process:", error);
      setMessage({ text: error instanceof Error ? error.message : "Error importing notes. Contact support@echonotes.ai.", type: 'error' });
      window.analytics?.track("Settings | Import Notes Failed", {
        error: error instanceof Error ? error.message : String(error)
      });
      setCurrentStep('upload');
    } finally {
      setIsImporting(false);
    }
  };

  const handleConfirmTopics = async () => {
    const selectedTopics = topics.filter(t => t.selected);
    
    if (selectedTopics.length === 0) {
      setCurrentStep('done');
      onSuccess?.();
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const currentTimestamp = formatISO(new Date());
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/notes/import/topics`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentTimestamp,
            topics: selectedTopics.map(({ name, noteIds }) => ({ name, noteIds })),
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }
      
      setCurrentStep('done');
      onSuccess?.();
    } catch (error) {
      console.error("Error confirming topics:", error);
      setMessage({ text: error instanceof Error ? error.message : "Error confirming topics. Contact support@echonotes.ai.", type: 'error' });
    }
  };

  const toggleTopic = (index: number) => {
    setTopics(prev => prev.map((topic, i) => 
      i === index ? { ...topic, selected: !topic.selected } : topic
    ));
  };

  const toggleAllTopics = () => {
    const allSelected = topics.every(topic => topic.selected);
    setTopics(prev => prev.map(topic => ({
      ...topic,
      selected: !allSelected
    })));
  };

  const handleDone = () => {
    onClose();
    setCurrentStep('upload');
    setTopics([]);
    setFiles([]);
    setMessage(defaultMessage);
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-[800px] sm:h-[600px] sm:p-6">
              <div className="h-full flex flex-col">
                {/* Step Indicator */}
                <div className="flex items-start pl-4 mb-6">
                  <div className="flex items-center">
                    <span className={`text-sm ${currentStep === 'upload' ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                      Step 1
                    </span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className={`text-sm ${currentStep === 'topics' ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                      Step 2
                    </span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className={`text-sm ${currentStep === 'done' ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                      Done
                    </span>
                  </div>
                </div>

                <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 pl-4 mb-6">
                  {currentStep === 'upload' && 'Upload Files'}
                  {currentStep === 'topics' && 'Topics'}
                  {currentStep === 'done' && 'Import Complete'}
                </Dialog.Title>

                <div className="flex-1 overflow-y-auto">
                  {currentStep === 'upload' && (
                    <div className="h-full">
                      <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors flex flex-col items-center justify-center h-full ${
                          isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p className="text-indigo-600">Drop the files here ...</p>
                        ) : (
                          <div className="flex flex-col items-center">
                            <ArrowUpTrayIcon className="h-8 w-8 text-gray-700 mb-4" />
                            <p className="text-gray-900">Click to select files to upload or drag and drop them here.</p>
                            <p className="text-sm text-gray-800 mt-2">Supported formats: .txt, .md</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {currentStep === 'topics' && (
                    <div className="flex flex-col h-full">
                      {/* Success Message */}
                      <div className="bg-green-50 rounded-lg p-4 mb-6 mx-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">Organized by existing topics</p>
                            <p className="text-sm text-green-700 mt-1">We sorted your notes into any existing relevant topics.</p>
                          </div>
                        </div>
                      </div>

                      {/* Topics List Header */}
                      <div className="flex items-center justify-between px-4 mb-3">
                        <h4 className="text-base font-semibold text-gray-900">Confirm new topics</h4>
                        <button
                          onClick={toggleAllTopics}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {topics.every(topic => topic.selected) ? 'Unselect All' : 'Select All'}
                        </button>
                      </div>

                      {/* Scrollable Topics List */}
                      <div className="overflow-y-auto">
                        {topics.map((topic, index) => (
                          <div key={index}>
                            <div className="border-t border-gray-200"></div>
                            <div 
                              className="py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 px-4"
                              onClick={() => toggleTopic(index)}
                            >
                              <div>
                                <p className="text-sm font-medium text-gray-900">{topic.name}</p>
                                <p className="text-sm text-gray-500 mt-1">{topic.noteCount} note{topic.noteCount === 1 ? '' : 's'}</p>
                              </div>
                              {topic.selected ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              ) : (
                                <div className="h-5 w-5 rounded-full border-2 border-gray-200"></div>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="border-t border-gray-200"></div>
                      </div>
                    </div>
                  )}

                  {currentStep === 'done' && (
                    <div className="h-full flex items-start justify-center">
                      <div className="bg-green-50 rounded-lg p-6 w-full mx-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <CheckCircleIcon className="h-6 w-6 text-green-500" />
                          </div>
                          <div className="ml-4">
                            <p className="text-lg font-medium text-green-800">Success!</p>
                            <p className="text-base text-green-700 mt-1">Your notes have been successfully imported and organized.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-end">
                  {currentStep === 'upload' && (
                    <p className={`text-sm mr-auto pl-4 ${message?.type === 'error' ? 'text-red-600' : 'text-gray-600'}`}>
                      {message?.text || (files.length > 0 ? `${files.length} ${files.length === 1 ? 'file' : 'files'} selected` : '')}
                    </p>
                  )}
                  <div className="flex space-x-3">
                    {currentStep !== 'done' && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        onClick={handleDone}
                      >
                        Cancel
                      </button>
                    )}
                    {currentStep === 'upload' && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md bg-gray-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 disabled:bg-gray-300"
                        disabled={files.length === 0 || isImporting}
                        onClick={handleImport}
                      >
                        {isImporting ? (
                          <>
                            Uploading...
                            <SpinningCircle size="sm" />
                          </>
                        ) : (
                          'Upload'
                        )}
                      </button>
                    )}
                    {currentStep === 'topics' && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md bg-gray-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600"
                        onClick={handleConfirmTopics}
                      >
                        {topics.some(t => t.selected) ? 'Confirm' : 'Skip'}
                      </button>
                    )}
                    {currentStep === 'done' && (
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                        onClick={handleDone}
                      >
                        Done
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImportNotes; 